import React, { useEffect } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import LoginAdmin2faForm from "./LoginAdmin2faForm";
import LoginAdminDefaultForm from "./LoginAdminDefaultForm";
import LoginTitle from "./LoginTitle";
import { loginRequest, logout, resetAuth } from "../../actions/LoginActions";
import useAbility from "../../hooks/useAbility";
import history from "../common/navigation/AppHistory";

import type { JSX } from "react";

import type { LoginFormValues, OtpFormValues } from "./loginSchemas";
import type { AppState } from "../../reducers";

interface LoginAdminProps {
  pathToResetPassword: string;
  redirectTo: string;
}

function LoginAdmin({
  pathToResetPassword,
  redirectTo,
}: LoginAdminProps): JSX.Element {
  const { t } = useTranslation("login");

  const isLoggedIn = useSelector(
    (state: AppState) => state.loginReducer.isLoggedIn,
  );

  const { search } = useLocation();
  const forceLogin = Boolean(new URLSearchParams(search).get("force"));

  const authenticationData = useSelector(
    (state: AppState) => state.loginReducer.authentication_data,
  );
  const token = useSelector((state: AppState) => state.loginReducer.token);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { getHomePageURL } = useAbility();
  const homePageUrl = getHomePageURL();

  useEffect(() => {
    queryClient.invalidateQueries();

    if (forceLogin && isLoggedIn) {
      dispatch(logout(token, null, "/login"));
      return;
    }

    if (forceLogin) {
      history.replace({
        pathname: "/login",
        search: "",
      });
      return;
    }

    if (isLoggedIn) {
      history.push(homePageUrl);
    }
  }, [dispatch, forceLogin, homePageUrl, isLoggedIn, queryClient, token]);

  const handleLoginSubmit = ({ email, password }: LoginFormValues): void => {
    dispatch(loginRequest({ email, password }, token, redirectTo));
  };

  const handleOtpSubmit = ({ otpToken }: OtpFormValues): void => {
    dispatch(
      loginRequest(
        {
          authentication_data: authenticationData,
          otp_token: otpToken,
        },
        token,
        redirectTo,
      ),
    );
  };

  const handleBackToSignin = (): void => {
    dispatch(resetAuth());
  };

  return (
    <React.Fragment>
      <LoginTitle>{t("loginTitle")}</LoginTitle>
      {authenticationData ? (
        <LoginAdmin2faForm
          backToSignin={handleBackToSignin}
          onSubmit={handleOtpSubmit}
        />
      ) : (
        <LoginAdminDefaultForm
          onSubmit={handleLoginSubmit}
          pathToResetPassword={pathToResetPassword}
        />
      )}
    </React.Fragment>
  );
}

export default LoginAdmin;
